import { localizationConfig } from "./localization-config";
import { DEFAULT_LOCALE } from "./locales";

const COOKIE_NAME = "redo_translations_locale";
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year

/**
 * Read the locale cookie from document.cookie.
 *
 * document.cookie returns all cookies as a single string: "a=1; b=2; c=3".
 * The regex matches our cookie name at the start of the string (^) or after
 * a "; " separator, then captures everything up to the next ";" as the value.
 */
export function getLocaleCookie(): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`),
  );
  return match?.[1] || undefined;
}

/**
 * Write the locale cookie.
 *
 * Assigning to document.cookie doesn't overwrite all cookies — the browser
 * API treats it as an upsert for the specific cookie name.
 *
 * - path=/        : available on all pages
 * - max-age=...   : persists for 1 year (in seconds)
 * - SameSite=Lax  : sent on same-site requests and top-level navigations
 */
export function setLocaleCookie(locale: string): void {
  if (typeof document === "undefined") {
    return;
  }
  document.cookie = `${COOKIE_NAME}=${locale}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`;
}

/**
 * Detect the best locale to use.
 * Precedence: preferred locale > cookie > browser preference > fallback (en)
 *
 * For browser preference, navigator.languages returns the user's preferred
 * languages in order (e.g. ["es-MX", "es", "en"]). We try an exact match
 * first, then fall back to the language prefix (e.g. "es-MX" → "es").
 */
export function getPreferredLocale(
  supportedLocales: string[],
  preferredLocale?: string | null,
): string {
  if (!localizationConfig.isEnabled()) {
    return DEFAULT_LOCALE;
  }

  if (preferredLocale && supportedLocales.includes(preferredLocale)) {
    return preferredLocale;
  }

  const fromCookie = getLocaleCookie();
  if (fromCookie && supportedLocales.includes(fromCookie)) {
    return fromCookie;
  }

  const languages =
    typeof navigator !== "undefined"
      ? navigator.languages || (navigator.language ? [navigator.language] : [])
      : [];
  for (const browserLang of languages) {
    const exact = browserLang.toLowerCase();
    if (supportedLocales.includes(exact)) {
      return exact;
    }
    // Try language prefix without region (e.g. "es-MX" → "es")
    const prefix = exact.split("-")[0]!;
    if (supportedLocales.includes(prefix)) {
      return prefix;
    }
  }

  return DEFAULT_LOCALE;
}
